import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Grid, Menu } from 'antd';
import { connect } from 'react-redux';
import { SIDE_NAV_LIGHT } from 'constants/ThemeConstant';
import utils from 'utils';
import { onMobileNavToggle } from 'redux/actions/Theme';
import { ReduxState } from '../../redux/reducers';
import classNames from 'classnames';
import { NavItem } from '../../layouts/app-layout/NavItem';
import { MenuItemProps } from 'antd/lib/menu/MenuItem';

const { useBreakpoint } = Grid;

const setDefaultOpen = (key: string) => {
  let keyList = [];
  let keyString = '';
  if (key) {
    const arr = key.split('-');
    for (let index = 0; index < arr.length; index++) {
      const elm = arr[index];
      index === 0 ? (keyString = elm) : (keyString = `${keyString}-${elm}`);
      keyList.push(keyString);
    }
  }
  return keyList;
};

interface SideNavContentProps {
  sideNavTheme: string;
  isNavEnabled: boolean;
  routeInfo: NavItem;
  navItems: NavItem[];
  hideGroupTitle?: boolean;
  onMobileNavToggle: (mobileNav: boolean) => void;
}

const SideNavContent = (props: SideNavContentProps) => {
  const { isNavEnabled, sideNavTheme, routeInfo, navItems, hideGroupTitle, onMobileNavToggle } = props;
  const isMobile = !utils.getBreakPoint(useBreakpoint()).includes('lg');
  const closeMobileNav = () => {
    if (isMobile) {
      onMobileNavToggle(false);
    }
  };
  const location = useLocation();
  const selectedKeys = navItems
    .flatMap((it) => [it, ...it.submenu.flatMap((it) => [it, ...it.submenu])])
    .filter((it) => location.pathname.startsWith(it.path))
    .map((it) => it.path);
  return (
    <Menu
      theme={sideNavTheme === SIDE_NAV_LIGHT ? 'light' : 'dark'}
      mode="inline"
      style={{ height: '100%', borderRight: 0 }}
      selectedKeys={selectedKeys}
      selectable={false}
      defaultOpenKeys={setDefaultOpen(routeInfo?.key)}
      className={classNames('user-select-none', hideGroupTitle && 'hide-group-title')}
    >
      {navItems.map((menu) => {
        return menu.submenu.length > 0 ? (
          <Menu.ItemGroup key={menu.key} title={<div className="ml-1">{menu.title}</div>}>
            {menu.submenu.map((subMenuFirst) =>
              subMenuFirst.submenu.length > 0 ? (
                <Menu.SubMenu
                  key={subMenuFirst.key}
                  title={
                    <NavMenuItemContent
                      isSelected={selectedKeys.includes(subMenuFirst.path)}
                      menu={subMenuFirst}
                      onLinkClick={() => closeMobileNav()}
                    />
                  }
                >
                  {subMenuFirst.submenu.map((subMenuSecond) => (
                    <NavMenuItem
                      key={subMenuSecond.path}
                      menu={subMenuSecond}
                      isNavEnabled={isNavEnabled}
                      onLinkClick={() => closeMobileNav()}
                    />
                  ))}
                </Menu.SubMenu>
              ) : (
                <NavMenuItem
                  key={subMenuFirst.path}
                  menu={subMenuFirst}
                  isNavEnabled={isNavEnabled}
                  onLinkClick={() => closeMobileNav()}
                />
              )
            )}
          </Menu.ItemGroup>
        ) : (
          <NavMenuItem key={menu.path} menu={menu} isNavEnabled={isNavEnabled} onLinkClick={() => closeMobileNav()} />
        );
      })}
    </Menu>
  );
};

type NavMenuItemProps = MenuItemProps & {
  isNavEnabled: boolean;
  menu: NavItem;
  onLinkClick: () => void;
};

const NavMenuItem: React.FC<NavMenuItemProps> = ({ menu, isNavEnabled, onLinkClick, ...menuProps }) => {
  return (
    <Menu.Item disabled={!isNavEnabled} className="bg-white px-4" {...menuProps}>
      <NavMenuItemContent className="pl-3" menu={menu} onLinkClick={onLinkClick} />
    </Menu.Item>
  );
};

interface NavMenuItemContentProps {
  className?: string;
  menu: NavItem;
  isSelected?: boolean;
  onLinkClick: () => void;
}

const NavMenuItemContent: React.FC<NavMenuItemContentProps> = ({ menu, isSelected, onLinkClick, className }) => {
  return (
    <div
      // For Intercom product tour.
      id={`side-nav${menu.path.replaceAll('/', '-')}`}
      className={classNames(
        'd-inline-flex align-items-center',
        isSelected && 'bg-green-1',
        isSelected && 'ant-menu-item-selected',
        className
      )}
      style={{ borderRadius: 4, width: '100%' }}
      onMouseEnter={menu.onMouseEnter}
    >
      {menu.icon ? <menu.icon isActive={true} /> : null}
      <span className="mr-2" />
      <span>{menu?.title}</span>
      <Link onClick={onLinkClick} to={menu.path} />
    </div>
  );
};

interface MenuContentProps {
  sideNavTheme: string;
  onMobileNavToggle: (mobileNav: boolean) => void;
  routeInfo: NavItem;
  switcher: React.ReactNode;
  navItems: NavItem[];
  isNavEnabled: boolean;
}

const MenuContent = (props: MenuContentProps) => {
  return (
    <div className="pt-3">
      <div className="px-4 d-flex">{props.switcher}</div>
      <SideNavContent {...props} />
    </div>
  );
};

const mapStateToProps = ({ theme }: ReduxState) => {
  const { sideNavTheme } = theme;
  return { sideNavTheme };
};

export default connect(mapStateToProps, { onMobileNavToggle })(MenuContent);
